import { ReactElement, useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { Page, CrudMode } from '@ohpen/enso-ui';
import { ConfirmCrDeleteModal, EXCLUSIVE_SETS_ENTITY_LIST_NAMES, ExclusiveProductsCrudForm, ExclusiveProductsList } from '@pmp/components';
import { useValueListsStore } from '@pmp/stores';
import { ExclusivitySetResponse, productExclusivitySetsCreateDeleteCr, productExclusivitySetsValidateDelete } from '@pmp/api';
import { usePmpFetch } from '@pmp/hooks';

import { initialState, reducer } from './reducer';

export const ExclusiveProductsPage = (): ReactElement => {
  const [isFetching, , data, , fetchData] = usePmpFetch('productExclusivitySetsGetAllSets');
  const [, , firms, , fetchFirms] = usePmpFetch('lenderFirmsGetAllLenderFirms');
  const [state, dispatch] = useReducer(reducer, initialState);
  const { valueLists, getValueLists } = useValueListsStore();
  const { t } = useTranslation();

  useEffect(() => {
    getValueLists({ alwaysFetch: EXCLUSIVE_SETS_ENTITY_LIST_NAMES });
  }, [getValueLists]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchFirms();
  }, [fetchFirms]);

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: ExclusivitySetResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEdit = useCallback((record: ExclusivitySetResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: ExclusivitySetResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  return (
    <Page>
      <Page.Title>{t('pmp.exclusiveProducts.title')}</Page.Title>
      <Page.Content>
        <ExclusiveProductsList
          isFetching={isFetching}
          valueLists={valueLists}
          data={data ?? []}
          onCreate={onCreate}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />
        {state.crud.open && state.crud.mode && (
          <ExclusiveProductsCrudForm
            valueLists={valueLists}
            firms={firms ?? []}
            mode={state.crud.mode}
            record={state.crud.record}
            refreshTable={fetchData}
            onClose={onCloseCrud}
          />
        )}
      </Page.Content>
      <ConfirmCrDeleteModal
        open={state.deleteModal.open}
        records={state.deleteModal.record ? [state.deleteModal.record] : []}
        idKey="id"
        nameKey="name"
        entityLabel={t('pmp.exclusiveProducts.labels.exclusiveProduct')}
        entityLabelPlural={t('pmp.exclusiveProducts.labels.exclusiveProducts')}
        getPrecheck={productExclusivitySetsValidateDelete}
        createDeleteChangeRequest={productExclusivitySetsCreateDeleteCr}
        refreshTable={fetchData}
        onClose={onCloseDeleteModal}
      />
    </Page>
  );
};
